import type {
  Dispatch,
  KeyboardEvent,
  KeyboardEventHandler,
  MouseEvent,
  MouseEventHandler,
  SetStateAction,
} from 'react'
import { useCallback, useRef, useState } from 'react'
import clamp from 'lodash/clamp'
import { getWindow, hasWindow } from '@ztechapps/common.utils'

/**
 * Keyboard navigation hook for a spreadsheet-style grid (Sheets-like keyboard model).
 *
 * Two cell modes:
 *   - VIEW: cell shows its value as plain text. Arrow keys move `activeCell` between cells.
 *     Enter / printable letter enters EDIT on the active cell.
 *   - EDIT: cell renders its input. A typed edit (seeded by a printable key) commits and moves
 *     on arrows unless the editor already handled the key (`event.defaultPrevented`); an
 *     Enter-started edit keeps every arrow inside the editor. Enter / Shift+Enter commit and move
 *     down / up. Esc reverts and exits. Tab commits, advances to next cell, returns to VIEW.
 *
 * Clicking another cell goes through `selectCell`, which ends an edit left open on any other
 * cell and keeps its value, then selects the clicked cell. A capture-phase mousedown blurs the
 * focused editor first when the press falls outside the editing cell, so typed text commits.
 *
 * One keydown handler is attached at the container level (via `containerProps.onKeyDown`) — cells
 * are non-interactive in view mode (just a focusable `<td>`); the active cell's `<td>` (view) or
 * its input (edit) receives DOM focus and the keypress bubbles to the container handler. This
 * eliminates the O(rows × cols) re-render that per-cell handlers caused on every arrow press.
 *
 * The hook is domain-agnostic — it only knows `rowCount × columnCount`. Consumers define their
 * own column identifiers (e.g. an enum mapping each column to an index) and pass them as numeric
 * indices into `getCellProps`. The `onDeleteEmptyRow`, `isCellEmpty`, `isRowEmpty`, `onEnterEdit`,
 * `onRevertEdit` callbacks let the consumer wire domain behavior (snapshot/revert, value
 * replacement on letter-key entry, empty-row deletion) without coupling the hook to a schema.
 */

export enum GridDirection {
  Up = 'Up',
  Down = 'Down',
  Left = 'Left',
  Right = 'Right',
}

const ARROW_KEY_DIRECTION: Record<string, GridDirection> = {
  ArrowUp: GridDirection.Up,
  ArrowDown: GridDirection.Down,
  ArrowLeft: GridDirection.Left,
  ArrowRight: GridDirection.Right,
}

export type ActiveCell = {
  row: number
  col: number
}

export type UseSpreadsheetNavigationProps = {
  rowCount: number
  columnCount: number
  onDeleteEmptyRow: (rowIndex: number) => void
  isCellEmpty?: (row: number, col: number) => boolean
  isRowEmpty?: (row: number) => boolean
  // Fires when the user enters edit mode on a cell. `initialChar` is the single character that
  // triggered the edit (e.g., 'W' for a letter key) or null if Enter was the trigger. Cell
  // consumers should snapshot the current value here for potential revert.
  onEnterEdit?: (row: number, col: number, initialChar: string | null) => void
  // Fires when the user presses Esc in edit mode. Cell consumers should restore the snapshot.
  onRevertEdit?: (row: number, col: number) => void
}

export type CellProps = {
  tabIndex: number
  isActive: boolean
  isEditing: boolean
  // The single character that triggered edit mode (if any). Cell consumers seed the input with
  // this character so typing 'W' in view mode lands in the input as 'W'. Null when Enter
  // triggered edit (no character to inject).
  initialEditChar: string | null
}

export type ContainerProps = {
  onKeyDown: KeyboardEventHandler<HTMLElement>
  onMouseDownCapture: MouseEventHandler<HTMLElement>
}

export type UseSpreadsheetNavigationResult = {
  activeCell: ActiveCell
  setActiveCell: Dispatch<SetStateAction<ActiveCell>>
  selectCell: (row: number, col: number) => void
  editingCell: ActiveCell | null
  enterEditMode: (row: number, col: number, initialChar?: string | null) => void
  exitEditMode: () => void
  getCellProps: (row: number, col: number) => CellProps
  containerProps: ContainerProps
  advanceFromCell: (row: number, col: number) => void
}

const moveCell = ({
  row,
  col,
  direction,
  rowCount,
  columnCount,
}: {
  row: number
  col: number
  direction: GridDirection
  rowCount: number
  columnCount: number
}): ActiveCell => {
  switch (direction) {
    case GridDirection.Up:
      return { row: clamp(row - 1, 0, rowCount - 1), col }
    case GridDirection.Down:
      return { row: clamp(row + 1, 0, rowCount - 1), col }
    case GridDirection.Left:
      return { row, col: clamp(col - 1, 0, columnCount - 1) }
    case GridDirection.Right:
      return { row, col: clamp(col + 1, 0, columnCount - 1) }
  }
}

const advanceTab = ({
  row,
  col,
  rowCount,
  columnCount,
  reverse,
}: {
  row: number
  col: number
  rowCount: number
  columnCount: number
  reverse: boolean
}): ActiveCell => {
  const linear = row * columnCount + col
  const next = reverse ? linear - 1 : linear + 1
  const max = rowCount * columnCount - 1
  const clamped = clamp(next, 0, max)
  return { row: Math.floor(clamped / columnCount), col: clamped % columnCount }
}

// A printable single character — letters, digits, punctuation. Excludes modifier-only events
// (Shift / Control / Meta) and special keys (ArrowDown / Tab / Enter / Backspace / Escape) which
// arrive with `event.key.length > 1`.
const isPrintableChar = (key: string): boolean => key.length === 1 && key !== ' '

export const useSpreadsheetNavigation = ({
  rowCount,
  columnCount,
  onDeleteEmptyRow,
  isCellEmpty,
  isRowEmpty,
  onEnterEdit,
  onRevertEdit,
}: UseSpreadsheetNavigationProps): UseSpreadsheetNavigationResult => {
  const [activeCell, setActiveCell] = useState<ActiveCell>({ row: 0, col: 0 })
  const [editingCell, setEditingCell] = useState<ActiveCell | null>(null)
  const [initialEditChar, setInitialEditChar] = useState<string | null>(null)

  // Refs mirror state so the stable container handler reads the latest values without re-binding.
  const activeRef = useRef(activeCell)
  activeRef.current = activeCell
  const editingRef = useRef(editingCell)
  editingRef.current = editingCell
  const initialEditCharRef = useRef(initialEditChar)
  initialEditCharRef.current = initialEditChar

  const enterEditMode = useCallback(
    (row: number, col: number, initialChar: string | null = null) => {
      setEditingCell({ row, col })
      setInitialEditChar(initialChar)
      onEnterEdit?.(row, col, initialChar)
    },
    [onEnterEdit]
  )

  const exitEditMode = useCallback(() => {
    setEditingCell(null)
    setInitialEditChar(null)
  }, [])

  const advanceFromCell = useCallback(
    (row: number, col: number) => {
      setActiveCell(advanceTab({ row, col, rowCount, columnCount, reverse: false }))
    },
    [rowCount, columnCount]
  )

  // Click path — ends an edit left open on another cell and keeps its value (never reverts),
  // then selects the clicked cell. Clicking the editing cell itself keeps the edit open.
  const selectCell = useCallback((row: number, col: number) => {
    const editing = editingRef.current
    if (editing && (editing.row !== row || editing.col !== col)) {
      setEditingCell(null)
      setInitialEditChar(null)
    }
    setActiveCell({ row, col })
  }, [])

  // Capture-phase mousedown — blurs the focused editor when the press lands outside the editing
  // cell, so the editor's own blur path commits typed text and closes the menu before the click
  // selects the clicked cell. Capture is required because the editor wrapper stops bubble-phase
  // mousedown, and the press is tested against the cell's rect because the open in-cell menu
  // stays a DOM child of the editing cell while overflowing it visually — subtree membership
  // cannot tell an outside press from a menu pick. A pick still lands: the blur commits nothing
  // without typed text, and the option's click fires on the still-mounted menu.
  const onContainerMouseDownCapture: MouseEventHandler<HTMLElement> = useCallback((event: MouseEvent<HTMLElement>) => {
    if (event.button !== 0 || !editingRef.current) {
      return
    }
    if (!hasWindow()) {
      return
    }
    const active = getWindow()?.document.activeElement as HTMLElement | null
    if (!active || typeof active.blur !== 'function' || typeof active.closest !== 'function') {
      return
    }
    const cell = active.closest('td')
    if (!cell) {
      return
    }
    const rect = cell.getBoundingClientRect()
    if (
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom
    ) {
      return
    }
    active.blur()
  }, [])

  const onContainerKeyDown: KeyboardEventHandler<HTMLElement> = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      const active = activeRef.current
      const editing = editingRef.current
      const { row, col } = active

      // EDIT MODE — Enter commits and moves down (up with Shift), Tab commits and advances,
      // Escape reverts. A typed edit's arrows commit and move unless the editor already handled
      // the key; an Enter-started edit keeps every arrow inside the editor. Enter, Tab and Escape
      // are always handled, whether or not the editor default-prevented them.
      if (editing) {
        switch (event.key) {
          case 'Escape':
            event.preventDefault()
            event.stopPropagation()
            onRevertEdit?.(editing.row, editing.col)
            setEditingCell(null)
            setInitialEditChar(null)
            return
          case 'Enter':
            event.preventDefault()
            setEditingCell(null)
            setInitialEditChar(null)
            setActiveCell(
              moveCell({
                row: editing.row,
                col: editing.col,
                direction: event.shiftKey ? GridDirection.Up : GridDirection.Down,
                rowCount,
                columnCount,
              })
            )
            return
          case 'Tab': {
            event.preventDefault()
            setEditingCell(null)
            setInitialEditChar(null)
            setActiveCell(
              advanceTab({ row: editing.row, col: editing.col, rowCount, columnCount, reverse: event.shiftKey })
            )
            return
          }
          case 'ArrowUp':
          case 'ArrowDown':
          case 'ArrowLeft':
          case 'ArrowRight': {
            if (initialEditCharRef.current === null || event.defaultPrevented) {
              return
            }
            event.preventDefault()
            setEditingCell(null)
            setInitialEditChar(null)
            setActiveCell(
              moveCell({
                row: editing.row,
                col: editing.col,
                direction: ARROW_KEY_DIRECTION[event.key],
                rowCount,
                columnCount,
              })
            )
            return
          }
          default:
            return
        }
      }

      // VIEW MODE
      switch (event.key) {
        case 'ArrowUp':
        case 'ArrowDown':
        case 'ArrowLeft':
        case 'ArrowRight':
          event.preventDefault()
          setActiveCell(moveCell({ row, col, direction: ARROW_KEY_DIRECTION[event.key], rowCount, columnCount }))
          return
        case 'Tab':
          event.preventDefault()
          setActiveCell(advanceTab({ row, col, rowCount, columnCount, reverse: event.shiftKey }))
          return
        case 'Enter':
          event.preventDefault()
          enterEditMode(row, col, null)
          return
        case 'Backspace': {
          // A shortcut combo (Ctrl+Shift+Backspace) is the host's, not an emptied-row delete.
          if (event.ctrlKey || event.metaKey || event.altKey) return
          const cellEmpty = isCellEmpty?.(row, col) === true
          const rowEmpty = isRowEmpty?.(row) === true
          if (cellEmpty && rowEmpty) {
            event.preventDefault()
            onDeleteEmptyRow(row)
          }
          return
        }
        default:
          // Printable letter / digit / punctuation → enter edit mode with that char as the seed.
          if (isPrintableChar(event.key) && !event.ctrlKey && !event.metaKey && !event.altKey) {
            event.preventDefault()
            enterEditMode(row, col, event.key)
          }
          return
      }
    },
    [rowCount, columnCount, onDeleteEmptyRow, isCellEmpty, isRowEmpty, onRevertEdit, enterEditMode]
  )

  const getCellProps = useCallback(
    (row: number, col: number): CellProps => {
      const isActive = activeCell.row === row && activeCell.col === col
      const isEditing = isActive && editingCell?.row === row && editingCell?.col === col
      return {
        tabIndex: isActive ? 0 : -1,
        isActive,
        isEditing,
        initialEditChar: isEditing ? initialEditChar : null,
      }
    },
    [activeCell, editingCell, initialEditChar]
  )

  return {
    activeCell,
    setActiveCell,
    selectCell,
    editingCell,
    enterEditMode,
    exitEditMode,
    getCellProps,
    containerProps: { onKeyDown: onContainerKeyDown, onMouseDownCapture: onContainerMouseDownCapture },
    advanceFromCell,
  }
}
